import { type JSX } from "react";

export type BannerTone = "success" | "error" | "warning";

/** Icon path plus the colour trio each tone renders in. */
const TONES: Record<BannerTone, { path: string; container: string; icon: string; text: string }> = {
  success: {
    path: "M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z",
    container: "bg-green-50",
    icon: "text-green-400",
    text: "text-green-800",
  },
  error: {
    path: "M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z",
    container: "bg-red-50",
    icon: "text-red-400",
    text: "text-red-800",
  },
  warning: {
    path: "M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z",
    container: "bg-yellow-50",
    icon: "text-yellow-400",
    text: "text-yellow-700",
  },
};

/** Border classes for the bordered variant, keyed by tone. */
const BORDERS: Record<BannerTone, string> = {
  success: "border border-green-200",
  error: "border border-red-200",
  warning: "border border-yellow-200",
};

/**
 * The icon-plus-message strip the upload pages use to report progress and
 * failures. `bordered` picks between the two variants already in use.
 */
export default function StatusBanner({
  tone,
  message,
  bordered = false,
}: {
  tone: BannerTone;
  message: string;
  bordered?: boolean;
}): JSX.Element {
  const { path, container, icon, text } = TONES[tone];

  return (
    <div className={`rounded-md p-4 ${container} ${bordered ? BORDERS[tone] : ""}`}>
      <div className="flex">
        <div className="flex-shrink-0">
          <svg
            className={`h-5 w-5 ${icon}`}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path fillRule="evenodd" d={path} clipRule="evenodd" />
          </svg>
        </div>
        <div className="ml-3">
          <p className={`text-sm font-medium ${text}`}>{message}</p>
        </div>
      </div>
    </div>
  );
}

/** The white spinner shown inside a busy submit button. */
export function ButtonSpinner(): JSX.Element {
  return (
    <svg
      className="animate-spin -ml-1 mr-3 h-5 w-5 text-white"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      ></path>
    </svg>
  );
}
